import React from 'react';
import { ArrowRight, Check, Award, Zap, Lock } from 'lucide-react';
import Section, { SectionTitle, SectionSubtitle } from '../components/ui/Section';
import Card, { CardHeader, CardTitle, CardContent } from '../components/ui/Card';
import Button from '../components/ui/Button';
import HeroSection from '../components/ui/HeroSection';
import HeroCard from '../components/ui/HeroCard';
import CTASection from '../components/ui/CTASection';
import FAQSection from '../components/ui/FAQSection';
import FeatureCard from '../components/ui/FeatureCard';

const PartnersPage = () => {
  return (
    <div id="top" className="font-sans text-black">
      {/* Hero Section */}
      <HeroSection
        title="Partner with Senti"
        description="Join forces with Senti to integrate our revolutionary Large Biosignal Model into your medical devices. Accelerate your time to market while delivering superior diagnostic capabilities."
        buttonText="Become a Partner"
        buttonLink="/contact"
        buttonVariant="primary"
        buttonScrollTo="contact"
        paddingY="py-32"
        centered={false}
      >
        <HeroCard 
          icon={<Award className="h-20 w-20 text-primary" />}
          title="Strategic Collaboration"
          description="Our partnership model is designed to minimize your upfront costs and technical burden while maximizing the capabilities of your devices."
        />
      </HeroSection>

      {/* Why Partner Section */}
      <Section>
        <SectionTitle>Why Partner with Senti?</SectionTitle>
        <SectionSubtitle>
          Our LBM technology offers significant advantages to medical device manufacturers, enabling you to create 
          transformative healthcare solutions without the burden of AI development.
        </SectionSubtitle>

        <div className="grid md:grid-cols-3 gap-10">
          <FeatureCard
            icon={<Zap className="h-7 w-7 text-primary" />}
            title="Accelerate Development"
            description="Reduce time to market by leveraging our pre-trained LBM, saving you years of AI development and millions in R&D costs."
            variant="card"
            className="transition-transform hover:scale-105"
          />
          
          <FeatureCard
            icon={<Award className="h-7 w-7 text-primary" />}
            title="Superior Performance"
            description="Achieve superhuman diagnostic capabilities with our purpose-built AI that outperforms traditional models in biosignal analysis."
            variant="card"
            className="transition-transform hover:scale-105"
          />
          
          <FeatureCard
            icon={<Lock className="h-7 w-7 text-primary" />}
            title="Regulatory Expertise"
            description="Benefit from our regulatory experience with FDA submissions and compliance requirements for AI-driven medical devices."
            variant="card"
            className="transition-transform hover:scale-105"
          />
        </div>
      </Section>

      {/* Partnership Model */}
      <Section background="light">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          <div>
            <h2 className="text-3xl font-bold text-primary mb-6">Our Partnership Model</h2>
            <p className="text-gray-700 mb-8">
              We've designed our partnership approach to minimize your upfront costs and technical burden 
              while maximizing the diagnostic capabilities of your devices.
            </p>

            <div className="space-y-4">
              <div className="flex items-start">
                <div className="mt-1 mr-4">
                  <Check className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold text-secondary">No-Cost R&D Integration</h3>
                  <p className="text-gray-600">Our LBM is provided at no cost during your research and development phase.</p>
                </div>
              </div>

              <div className="flex items-start">
                <div className="mt-1 mr-4">
                  <Check className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold text-secondary">Revenue-Share Commercialization</h3>
                  <p className="text-gray-600">A simple revenue-share model once your product reaches the market (typically ~10% of revenue).</p>
                </div>
              </div>

              <div className="flex items-start">
                <div className="mt-1 mr-4">
                  <Check className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold text-secondary">Technical Support</h3>
                  <p className="text-gray-600">Ongoing technical support and model updates to ensure your device maintains state-of-the-art performance.</p>
                </div>
              </div>

              <div className="flex items-start">
                <div className="mt-1 mr-4">
                  <Check className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold text-secondary">Regulatory Assistance</h3>
                  <p className="text-gray-600">Support with regulatory documentation and submissions related to the LBM component.</p>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white p-8 rounded-xl shadow-lg">
            <h3 className="text-2xl font-bold text-primary mb-6 text-center">Partnership Process</h3>
            
            <div className="space-y-8">
              <div className="relative flex items-start">
                <div className="absolute top-0 left-7 h-full w-0.5 bg-accent" style={{ top: '24px' }}></div>
                <div className="h-14 w-14 rounded-full bg-primary text-white flex items-center justify-center flex-shrink-0 z-10">
                  <span className="font-bold text-lg">1</span>
                </div>
                <div className="ml-6">
                  <h4 className="font-semibold text-lg text-secondary">Initial Consultation</h4>
                  <p className="text-gray-600">We'll discuss your device, goals, and how our LBM can enhance its capabilities.</p>
                </div>
              </div>
              
              <div className="relative flex items-start">
                <div className="absolute top-0 left-7 h-full w-0.5 bg-accent" style={{ top: '24px' }}></div>
                <div className="h-14 w-14 rounded-full bg-primary text-white flex items-center justify-center flex-shrink-0 z-10">
                  <span className="font-bold text-lg">2</span>
                </div>
                <div className="ml-6">
                  <h4 className="font-semibold text-lg text-secondary">Technical Integration</h4>
                  <p className="text-gray-600">Our engineering team works with yours to integrate the LBM into your device or software.</p>
                </div>
              </div>
              
              <div className="relative flex items-start">
                <div className="absolute top-0 left-7 h-full w-0.5 bg-accent" style={{ top: '24px' }}></div>
                <div className="h-14 w-14 rounded-full bg-primary text-white flex items-center justify-center flex-shrink-0 z-10">
                  <span className="font-bold text-lg">3</span>
                </div>
                <div className="ml-6">
                  <h4 className="font-semibold text-lg text-secondary">Validation & Testing</h4>
                  <p className="text-gray-600">Rigorous testing to ensure the LBM performs optimally with your specific application.</p>
                </div>
              </div>
              
              <div className="relative flex items-start">
                <div className="h-14 w-14 rounded-full bg-primary text-white flex items-center justify-center flex-shrink-0 z-10">
                  <span className="font-bold text-lg">4</span>
                </div>
                <div className="ml-6">
                  <h4 className="font-semibold text-lg text-secondary">Market Launch</h4>
                  <p className="text-gray-600">Coordinated launch support including technical documentation and marketing materials.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Section>

      {/* Current Partners Section */}
      <Section background="light">
        <SectionTitle>Our Current Partners</SectionTitle>
        <SectionSubtitle>
          We've established strategic partnerships with innovative medical device companies to bring the power of our LBM to market.
        </SectionSubtitle>
        
        <div className="grid md:grid-cols-3 gap-8">
          <div className="bg-white rounded-lg shadow-lg overflow-hidden transition-transform hover:scale-105 flex flex-col">
            <div className="bg-secondary h-3"></div>
            <div className="p-6 flex flex-col h-full">
              <div>
                <div className="mb-4 h-16 flex items-center justify-center">
                  <span className="text-2xl font-bold text-secondary">Bitbaby</span>
                </div>
                <h3 className="text-xl font-bold text-secondary h-16 mb-4">Fetal Wellbeing Monitoring</h3>
                <p className="text-gray-700 min-h-[80px]">
                  Bitbaby utilizes Senti's LBM to analyze fetal movements, providing a reliable measure of fetal health and wellbeing.
                </p>
              </div>
              <div className="border-t border-gray-200 pt-4 mt-4 w-full">
                <p className="text-secondary font-semibold">Impact:</p>
                <p className="text-gray-700">Helping to reduce stillbirths among the 12M pregnancies in high-income countries each year.</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-lg shadow-lg overflow-hidden transition-transform hover:scale-105 flex flex-col">
            <div className="bg-primary h-3"></div>
            <div className="p-6 flex flex-col h-full">
              <div>
                <div className="mb-4 h-16 flex items-center justify-center">
                  <span className="text-2xl font-bold text-primary">Arthritect</span>
                </div>
                <h3 className="text-xl font-bold text-primary h-16 mb-4">Rheumatoid Arthritis Diagnosis</h3>
                <p className="text-gray-700 min-h-[80px]">
                  The Arthritect system leverages our LBM to accelerate the diagnosis of rheumatoid arthritis from months or years down to weeks.
                </p>
              </div>
              <div className="border-t border-gray-200 pt-4 mt-4 w-full">
                <p className="text-primary font-semibold">Impact:</p>
                <p className="text-gray-700">Could unlock over £1.5 billion in direct healthcare savings annually through earlier detection and treatment.</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-lg shadow-lg overflow-hidden transition-transform hover:scale-105 flex flex-col">
            <div className="bg-accent h-3"></div>
            <div className="p-6 flex flex-col h-full">
              <div>
                <div className="mb-4 h-16 flex items-center justify-center">
                  <span className="text-2xl font-bold text-gray-800">Your Company Here</span>
                </div>
                <h3 className="text-xl font-bold text-gray-800 h-16 mb-4">Join Our Partner Network</h3>
                <p className="text-gray-700 min-h-[80px]">
                  We're actively seeking innovative partners to bring the power of our LBM to new medical applications.
                </p>
              </div>
              <div className="border-t border-gray-200 pt-4 mt-4 w-full">
                <p className="text-gray-800 font-semibold">Next Steps:</p>
                <Button to="/contact" variant="primary" scrollTo="contact" className="mt-2">Contact Us</Button>
              </div>
            </div>
          </div>
        </div>
      </Section>

      {/* FAQ Section */}
      <FAQSection
        faqs={[
          {
            question: "What types of medical devices can integrate with Senti's LBM?",
            answer: "Our LBM is designed to work with any device that captures biosignals, including respiratory sounds, bioimpedance, ECG, EEG, motion sensors, and more. If your device captures any form of biological signal, our LBM can likely enhance its diagnostic capabilities."
          },
          {
            question: "How long does integration typically take?",
            answer: "Integration timelines vary based on the complexity of your device and the specific application, but typically range from 3-6 months. This is significantly faster than building your own AI solution, which can take years."
          },
          {
            question: "What are the technical requirements for integration?",
            answer: "Our LBM can be deployed in various ways, including cloud-based API integration or on-device implementations for edge computing. We work with your technical team to determine the best approach based on your specific needs."
          },
          {
            question: "How does the revenue-share model work?",
            answer: "We typically structure partnerships with a modest revenue share (approximately 10% of device revenue) once your product reaches market. This aligns our incentives with yours and eliminates the need for large upfront licensing fees."
          }
        ]}
      />

      {/* CTA Section */}
      <CTASection
        title="Ready to Transform Healthcare Together?"
        description="Join the vanguard of medical device innovation with Senti's groundbreaking Large Biosignal Model. Let's build the future of healthcare diagnostics together."
        buttonText="Start the Conversation"
        buttonLink="/contact"
        buttonVariant="white"
        scrollToId="top"
        background="secondary"
      />
    </div>
  );
};

export default PartnersPage;